import api from '../utils/api';

class PredefineTemplateService {
  // List Folder Templates (Super Admin)
  async getTemplates(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        per_page: params.per_page || 20,
        ...(params.search && { search: params.search }),
        ...(params.is_active !== undefined && { is_active: params.is_active }),
        sort_by: params.sort_by || 'name',
        sort_order: params.sort_order || 'asc'
      });

      const response = await api.get(`/admin/folder-templates?${queryParams}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Get Template (Super Admin)
  async getTemplate(id) {
    try {
      const response = await api.get(`/admin/folder-templates/${id}`);
      console.log('Get Template API Response:', response);
      // Return the whole response to preserve the structure
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Create Folder Template (Super Admin)
  async createTemplate(data) {
    try {
      const payload = {
        name: data.name,
        description: data.description || '',
        is_active: data.is_active !== undefined ? data.is_active : true,
        folder_structure: data.folder_structure || []
      };

      const response = await api.post('/admin/folder-templates', payload);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Update Folder Template (Super Admin)
  async updateTemplate(id, data) {
    try {
      const payload = {
        name: data.name,
        description: data.description || '',
        is_active: data.is_active !== undefined ? data.is_active : true,
        folder_structure: data.folder_structure || []
      };

      const response = await api.put(`/admin/folder-templates/${id}`, payload);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Delete template (Super Admin)
  async deleteTemplate(id) {
    try {
      const response = await api.delete(`/admin/folder-templates/${id}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Toggle Folder Template Status (Super Admin)
  async toggleTemplateStatus(id) {
    try {
      const response = await api.post(`/admin/folder-templates/${id}/toggle`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Import template to dataroom (This is for dataroom owners, not super admin)
  async importTemplate(templateId, dataroomId, options = {}) {
    try {
      const response = await api.post(`/datarooms/${dataroomId}/import-template`, {
        template_id: templateId,
        target_folder: options.target_folder || null
      });
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Get template preview (Super Admin)
  async getTemplatePreview(id) {
    try {
      const response = await api.get(`/admin/folder-templates/${id}`);
      // Return the template object if it's nested, otherwise return the whole response
      return response.data?.template || response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Get template categories
  async getCategories() {
    try {
      const response = await api.get('/predefined-templates/categories');
      return response.data || response;
    } catch (error) {
      // Return default categories if API fails
      return [
        { value: 'general', label: 'General' },
        { value: 'legal', label: 'Legal' },
        { value: 'financial', label: 'Financial' },
        { value: 'hr', label: 'Human Resources' },
        { value: 'project', label: 'Project Management' },
        { value: 'compliance', label: 'Compliance' },
        { value: 'other', label: 'Other' }
      ];
    }
  }

  // Download template structure as JSON (Super Admin)
  async downloadTemplate(id) {
    try {
      const template = await this.getTemplate(id);
      const dataStr = JSON.stringify(template.data || template, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

      const link = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', `template_${id}_structure.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    } catch (error) {
      throw error;
    }
  }

  // Duplicate template (Super Admin)
  async duplicateTemplate(id) {
    try {
      // Get the original template
      const template = await this.getTemplate(id);
      const templateData = template.data || template;

      // Create a new template with duplicated data
      const duplicateData = {
        name: `${templateData.name} (Copy)`,
        description: templateData.description || '',
        is_active: false, // Set as inactive by default
        folder_structure: templateData.folder_structure || []
      };

      const response = await this.createTemplate(duplicateData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // List Available Templates for Dataroom Owners
  async getAvailableTemplatesForDataroom(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        per_page: params.per_page || 20,
        ...(params.search && { search: params.search })
      });

      const response = await api.get(`/datarooms/folder-templates?${queryParams}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }

  // Get Template for Dataroom Owners
  async getTemplateForDataroom(id) {
    try {
      const response = await api.get(`/datarooms/folder-templates/${id}`);
      return response.data || response;
    } catch (error) {
      throw error;
    }
  }
}

// Create singleton instance
const predefineTemplateService = new PredefineTemplateService();

export default predefineTemplateService;